
  import { dataNameProp, dataTitleProp, filterListToOptions, sequence } from '@/ui/helpers';
  import { Loader } from '@/ui/components/Loader';
  import { Box } from '@/ui/components/Box';
  import { ForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
  import { computed, defineComponent, ref, watch } from 'vue';
  import { hasPermission, Permissions } from '@/ui/auth/auth';
  import { useScheduleCheck } from '@/ui/pages/scheduleManager/useScheduleCheck';
  import { t } from '@/ui/i18n/i18n';
  import ScheduleEmpty from '@/ui/pages/scheduleManager/ScheduleEmpty.vue';
  import BulkDeleteButton from '@/ui/components/button/BulkDeleteButton.vue';
  import { useBulkDeletable } from '@/ui/pages/useBulkDeletable';
  import { bulkDelete, list as getScheduleList, remove } from '@/services/api/resources/schedule';
  import { ScheduleList } from '@/entities/schedule';
  import { emptyList } from '@/services/api';
  import { usePagination, useSorting, useTask } from '@/ui/concerns';
  import { filter as getAllUsers } from '@/services/api/resources/user';
  import { filter as getAllEntityItems } from '@/services/api/resources/entityItem';
  import { filter as getAllChecklists } from '@/services/api/resources/checklist';
  import { SelectOptions, SortDirection } from '@/ui/types';
  import * as qs from '@/ui/queryState';
  import { InputField } from '@/ui/components/form';
  import { updateMultiple } from '@/ui/queryState';
  import { BulkDeletableTable } from '@/ui/components/table/BulkDeletableTable';
  import { upperFirst } from 'lodash';
  import { TagField } from '@/ui/components/form/TagField';
  import ListTruncatedCell from '@/ui/components/table/ListTruncatedCell.vue';
  import CloneSchedule from '@/ui/pages/scheduleManager/CloneSchedule.vue';
  import { Pagination } from '@/ui/components/Pagination';

  export default defineComponent({
    name: 'ScheduleIndexTab',
    components: {
      TagField,
      BulkDeletableTable,
      InputField,
      BulkDeleteButton,
      ScheduleEmpty,
      ForbiddenPage,
      Loader,
      Box,
      ListTruncatedCell,
      CloneSchedule,
      Pagination,
    },
    setup() {
      const canAccessIndexPage = hasPermission(Permissions.MANAGE_CHECKLISTS);

      // state stored in query string
      const { page, perPage, resetPagination } = usePagination();
      const { sort, dir } = useSorting('createdAt', SortDirection.Descending);
      const { checkResult, taskReportCheck } = useScheduleCheck();

      // get filters task
      const search = qs.string('search', '');
      const assigneesFilter = qs.numbers('assignees', []);
      const assignees = ref<SelectOptions>([]);
      const entitiesFilter = qs.numbers('entities', []);
      const entityItems = ref<SelectOptions>([]);
      const checklistsFilter = qs.numbers('checklists', []);
      const checklists = ref<SelectOptions>([]);
      const getFilters = useTask(
        () => sequence(getAllUsers(), getAllEntityItems(), getAllChecklists()),
        ([userList, entityItemsList, checklistsList]) => {
          assignees.value = filterListToOptions(userList, t('common.no-title'));
          entityItems.value = filterListToOptions(entityItemsList, t('common.no-title'));
          checklists.value = filterListToOptions(checklistsList, t('common.no-title'));
        },
      );

      const updateSearch = (value: string) => updateMultiple(() => {
        resetPagination();
        search.value = value;
      }, true);

      const updateAssignees = (value: number[]) => updateMultiple(() => {
        resetPagination();
        assigneesFilter.value = value;
      }, true);

      const updateEntities = (value: number[]) => updateMultiple(() => {
        resetPagination();
        entitiesFilter.value = value;
      }, true);

      const updateChecklists = (value: number[]) => updateMultiple(() => {
        resetPagination();
        checklistsFilter.value = value;
      }, true);

      // get list task
      const list = ref<ScheduleList>(emptyList());
      const getList = useTask(
        () => getScheduleList({
          page: page.value,
          perPage: perPage.value,
          search: search.value,
          entities: entitiesFilter.value,
          assignees: assigneesFilter.value,
          checklists: checklistsFilter.value,
          sort: sort.value,
          dir: dir.value,
        }),
        listResponse => {
          list.value = listResponse;
        },
      );

      watch([
        page,
        perPage,
        search,
        assigneesFilter,
        entitiesFilter,
        checklistsFilter,
        sort,
        dir,
      ], getList.run);

      const { selectedResources, deleteBulkTask, onCheckedRowsUpdate } = useBulkDeletable({
        getList,
        bulkDeleteFn: bulkDelete,
      });

      const isDeleteBulkTaskPending = computed(() => deleteBulkTask.state.value.pending);

      const isRowCheckable = (row: BaseEntity) => row.authorizedTo.delete;

      return {
        canAccessIndexPage,
        checkResult,
        taskReportCheck,
        selectedResources,
        deleteBulkTask,
        onCheckedRowsUpdate,
        search,
        updateSearch,
        getList,
        list,
        page,
        perPage,
        isDeleteBulkTaskPending,
        removeSchedule: remove,
        isRowCheckable,
        dataTitleProp,
        dataNameProp,
        upperFirst,
        getFilters,
        assigneesFilter,
        assignees,
        entitiesFilter,
        entityItems,
        checklistsFilter,
        checklists,
        updateChecklists,
        updateAssignees,
        updateEntities,
      };
    },
  });
